// Other plugins.
import { logManager } from '../logs'

// Utils.
import { toQueryString } from '../common/utils'

/**
 * The possible response data types that can be handled.
 */
const responseTypes = Object.freeze({
  json: 'json',
  blob: 'blob',
  text: 'text',
  none: 'none'
})

/**
 * Make a request with the specified options. The options is very similar to the options passed to the GlobalFetch
 * method except that is also accepts the url as part of the options.
 * Reference: https://developer.mozilla.org/en-US/docs/Web/API/Request
 *
 * Currently this processes the request and assumes nothing about the response.
 * - If the response has a body, it will always be parsed and forwarded on.
 *      - If no body, then use an empty object in its place.
 * - If the fetch succeeded, the response "results" will always be forwarded on,
 *      even if the response is outside of the 200-299 range.
 *      - Because some backends provide more detailed error info (that a saga
 *        may need) as part of the body, rather than just the response status.
 * - If the fetch fails, return with the fetch failure information.
 *
 * @param {Object} options Options to make the request with.
 * @param {string} options.url The URL to make the request to.
 * @param {Object} [options.queryParams] The parameters to be added to the query string
 * @param {string} [options.responseType='json] The expected data type of the response body.
 * @param {Blob|BufferSource|FormData|UrlSearchParams|string} [options.body] The request body
 * @return {Promise} A promise that resolves with a custom response object.
 */
export default async function makeRequest (options, requestId) {
  const log = logManager.getLogger('REQUEST', requestId)

  /*
   * Check the provided options to ensure they are valid before making the request.
   */
  const error = validateOptions(options)
  if (error) {
    log.info(error.result.message)
    return error
  }

  // Extract and remove the non-fetch API properties.
  const { url, queryParams, responseType = 'json', ...fetchOptions } = options

  // Grab that last part of the URL (after the last /) to be logged.
  let endUrl = url.match(/([^/]*)$/)[0]
  // Cut it short if it's too long, since this should be human-readable.
  endUrl = endUrl.length > 15 ? endUrl.substring(0, 15) + '...' : endUrl
  log.info(`Making ${fetchOptions.method} ${endUrl} request.`)

  let response
  /*
   * Make the request. Scenarios to check for:
   *  1. Fetch failed.
   *  2. Fetch succeeded and is ok, with no response body.
   *  3. Fetch succeeded and is ok, with expected response type.
   *  4. Fetch succeeded and is ok, with unexpected response type.
   *  5. Fetch succeeded but is not ok.
   */
  try {
    response = await fetch(url + toQueryString(queryParams), fetchOptions)
  } catch (err) {
    // Scenario 1: Fetch failed.
    log.info(`Failed to make request, caused by ${err.message}`)
    return makeResponse({ error: 'FETCH' }, { code: err.name, message: err.message })
  }

  // Basic information for the request result.
  const result = {
    ok: response.ok,
    code: response.status,
    message: response.statusText
  }

  if (response.ok) {
    if (response.status === 204) {
      /*
       * A `204 (No Content)` response indicates a success, but with no content to return.
       * Avoid parsing the response because there isn't one.
       */
      // Scenario 2: Response is ok, with no response body.
      log.info(`Finished request with successful response (status ${response.status}).`)
      return makeResponse(undefined, { body: {}, ...result })
    }

    const data = await parseBody(response, responseType)
    if (data instanceof Error) {
      // Scenario 4: Response is ok, with an unexpected type.
      log.error(`Failed to parse with response type: ${responseType}. Error: ${data}`)
      return makeResponse({ error: 'REQUEST' }, { code: data.name, message: data.message })
    } else {
      // Scenario 3: Response is ok, with an expected type.
      log.info(`Finished request with successful response (status ${response.status}).`)
      return makeResponse(undefined, { body: data, ...result })
    }
  } else {
    // Scenario 5: Response is not ok.
    let contentType
    // Check the Content-Type of the response.
    try {
      contentType = await response.headers.get('content-type')
    } catch (err) {
      log.debug(`Failed to get content-type:${err.message}.`)
    }
    /*
     * Handle a SPiDR special-case error where the response body is a HTML page...
     * Throw away the body and simply report it as 'Forbidden'.
     */
    if (response.status === 403 && contentType.includes('html')) {
      return makeResponse({ error: 'REQUEST' }, result)
    }

    /*
     * Try to parse the response as JSON, and if successful, include
     * it in the returned error. otherwise, provide an empty object instead.
     */
    let responseBody
    try {
      responseBody = await response.json()
      if (response.status === 503) {
        let retryValue
        if (responseBody && responseBody.subscribeResponse) {
          // Try to get the retry value from the body of the response.
          // If it is in the body, then its value is a number
          retryValue = responseBody.subscribeResponse.retryAfter
        }

        let retryAfter // the final value to be used
        if (retryValue) {
          retryAfter = retryValue // should already be in milli seconds
        } else {
          // Try to get the retry value from `Retry-After` Header of the response.
          // This is the case when 503 response comes from an HTTP reverse proxy/load balancer node.
          try {
            // If provided as part of 'Retry-After' Header, its value will always be provided as a string
            retryValue = await response.headers.get('retry-after')
          } catch (err) {
            log.debug(`Failed to get retry-after:${err.message}.`)
          }

          if (retryValue) {
            // We have a value (either from body or from header of that reply).
            if (retryValue.endsWith(' GMT')) {
              // It is provided as `HTTP-date` format,
              // according to Standards spec: https://www.rfc-editor.org/rfc/rfc9110.html#section-10.2.3
              // If so, convert it to `number` format.
              const futureDate = new Date(retryValue)
              retryAfter = futureDate.getTime() - Date.now() // milliseconds
            } else {
              // it is provided in header as seconds
              retryAfter = retryValue * 1000 // automatic convertion from string to number, in milliseconds
            }
          }
        }

        // Ensure that no matter where it comes from, we always have the final value(s) saved in the parsed body,
        // as this is always used for later decision making.
        // NOTE: No guarantees on the existence of retryAfter,
        //       as HTTP reverse proxy/load balancer does not guarantee to provide one
        //       (see `SPiDR REST Overload Controls - Functional Description` spec).
        if (retryAfter && retryAfter > 0) {
          // retry value is available & valid
          responseBody = { subscribeResponse: { statusCode: 503, retryAfter } }
        } else {
          // The spec says that if we got a 503 but with no retryAfter available, then treat the reply as 500 error.
          responseBody = { subscribeResponse: { statusCode: 500 } }
        }
      }
    } catch (err) {
      responseBody = {}
      log.debug('Failed to parse response:', err.message)
    }
    return makeResponse({ error: 'REQUEST' }, { body: responseBody, ...result })
  }
}

/**
 * Checks for the provided options to be considered valid before making the
 *    request. If an option is invalid, an error response will be returned.
 * @param  {Object} options
 * @return {Response|undefined}
 */
function validateOptions (options) {
  const { url, responseType } = options

  if (!url || typeof url !== 'string') {
    const invalidUrlMessage = `Invalid request url; expected url of type string but received ${url} instead`
    return makeResponse({ error: 'REQUEST_URL', message: invalidUrlMessage })
  }

  /*
   * If an expected responseType was provided, it should be one of the types
   *    that we know how to handle.
   */
  if (responseType && !responseTypes.hasOwnProperty(responseType)) {
    const invalidResponseType = 'Cannot make request; responseType value was invalid.'
    return makeResponse({ error: 'RESPONSE_TYPE', message: invalidResponseType })
  }
}

/**
 * Get the body from the response depending on our expected content-type.
 * @return {Body|Error}
 */
async function parseBody (response, expectedType) {
  /**
   * The SDK should only be parsing the responses as is expected without checking the content type of the response.
   * This is deterministic depending on the `responseType` passed in to the request through the request options.
   */
  let responseBody = {}
  try {
    switch (expectedType) {
      case responseTypes.json:
        responseBody = await response.json()
        break
      case responseTypes.blob:
        responseBody = await response.blob()
        break
      case responseTypes.text:
        responseBody = await response.text()
        break
      case responseTypes.none:
        // Do not parse the response
        break
      default:
        // This should be unreachable code.
        throw Error('Assertion failed')
    }
  } catch (e) {
    // Note: We get here if we have a successful request but the server sent us unexpected data.
    // We need to treat this as an error case because we can no longer enforce a contract with the
    // code making the request.
    //
    // If the code gets here the issue is either with the server not sending us expected data, or with
    // the calling code that told us to expect the wrong data.

    return e
  }

  return responseBody
}

/**
 * Make a response object that will  have the same structure every time
 * regardless of the server response.
 *
 * @param {Object} apiResponse API related response data
 * @param {string} apiResponse.error This should be a string indicating an error if the request fails due to an invalid request.
 * @param {string} apiResponse.message This should be a string with more details about the api error.
 * @param {Object} httpResponse This will contain the response data from the server.
 * @param {Object} httpResponse.body The response body data from the server.
 * @param {boolean} httpResponse.ok Indicates if the request was considered a success by the server.
 * @param {Object} httpResponse.code The HTTP status code for the request.
 * @param {Object} httpResponse.message A message describing the server response.
 * @return {Object} An object containing API and/or server response details.
 */
function makeResponse (apiResponse = {}, httpResponse = {}) {
  return {
    body: httpResponse.body,
    error: apiResponse.error,
    result: {
      ok: Boolean(httpResponse.ok),
      code: httpResponse.code,
      message: apiResponse.message || httpResponse.message
    }
  }
}
